import { ReactNode } from "react";
import { Box, CircularProgress, Fab, IconButton, Stack, Typography } from "@mui/material";
import { ArrowUpwardRounded, CloseRounded, StopRounded } from "@mui/icons-material";
import voiceRecordingBarStyles from "./voiceRecordingBarStyles";

type VoiceRecordingBarProps = {
  visible: boolean;
  isTranscribing: boolean;
  canCancel: boolean;
  statusMessage: string;
  waveform: ReactNode;
  onCancel: () => void;
  onStop: () => void;
  onSend: () => void;
};

export default function VoiceRecordingBar({
  visible,
  isTranscribing,
  canCancel,
  statusMessage,
  waveform,
  onCancel,
  onStop,
  onSend,
}: VoiceRecordingBarProps) {
  return (
    /* Hidden instead of unmounted: the bar hosts the waveform, which owns the live recorder —
       it must already be mounted while the permission prompt is open and stay mounted through
       a cancelled session's wind-down. */
    <Stack direction={"row"} spacing={{ xs: 0.5, sm: 1 }} sx={[voiceRecordingBarStyles.bar, !visible && { display: "none" }]}>
      <IconButton onClick={onCancel} disabled={!canCancel} size={"small"} sx={voiceRecordingBarStyles.controlButton}>
        <CloseRounded />
      </IconButton>
      {isTranscribing && (
        <Stack direction={"row"} spacing={{ xs: 0.5, sm: 1 }} sx={voiceRecordingBarStyles.content}>
          <CircularProgress size={18} color={"inherit"} />
          <Typography variant={"body2"} noWrap>
            {statusMessage}
          </Typography>
        </Stack>
      )}
      {/* The waveform stays mounted while hidden: it hosts the recorder, and unmounting it
          before the recorder's stop event would lose the final audio chunk of a requested stop. */}
      <Box sx={[voiceRecordingBarStyles.waveform, isTranscribing && { display: "none" }]}>{waveform}</Box>
      <IconButton
        onClick={onStop}
        disabled={isTranscribing}
        size={"small"}
        sx={[voiceRecordingBarStyles.controlButton, voiceRecordingBarStyles.stopButton]}
      >
        <StopRounded />
      </IconButton>
      <Fab onClick={onSend} disabled={isTranscribing} size={"small"} color={"primary"}>
        <ArrowUpwardRounded />
      </Fab>
    </Stack>
  );
}
